/**
 * Web Task Admin API Client (contract v1.1)
 * Endpoints per WEB_TASKS_ADMIN_README.md §4.1–§4.9 — base: /admin/web-task
 * (alias: /admin/web-tasks)
 *
 *  - GET    /admin/web-task                  list tasks (§4.1, alias /list;
 *                                             `perPage` accepted as `limit` alias)
 *  - POST   /admin/web-task/create           create task (§4.2, alias POST /)
 *  - PUT    /admin/web-task/:id              update task (§4.3, id or taskId)
 *  - PATCH  /admin/web-task/:id/status       toggle isActive (§4.4 — omitting
 *                                             isActive inverts the state)
 *  - DELETE /admin/web-task/:id              soft delete (§4.5, alias /delete/:id)
 *  - GET    /admin/web-task/detail/:id       single task with stats (§4.6, alias /:id)
 *  - GET    /admin/web-task/summary          global session summary (§4.7 —
 *                                             byStatus always lists all 7 values)
 *  - GET    /admin/web-task/sessions         session audit list (§4.8, alias
 *                                             /history; `includeOpen` adds in-flight
 *                                             sessions; `INSUFFICIENT` is a filter alias)
 *  - GET    /admin/web-task/user/:userId     per-user web task data (§4.9)
 *
 * Response convention: admin endpoints return `status: true/false` at the top
 * level (not `success`); `data` / `pagination` shapes are shared with the
 * user-facing module. Pagination: { totalItems, currentPage, totalPages,
 * perPage, hasNextPage }.
 */

const getBaseUrl = () => {
  const envUrl =
    process.env.REACT_APP_API_BASE_URL || "https://api.minezeta.com/admin";
  return envUrl.replace(/\/+$/, "");
};

/** Endpoint builder — mirrors userNotificationApi.js convention */
const getEndpoint = (path = "") => {
  const base = getBaseUrl();
  const cleanPath = path.replace(/^\/+/, "");
  if (base.endsWith("/admin")) {
    return `${base}/web-task${cleanPath ? `/${cleanPath}` : ""}`;
  }
  return `${base}/admin/web-task${cleanPath ? `/${cleanPath}` : ""}`;
};

const getAuthHeaders = (isFormData = false) => {
  const token =
    localStorage.getItem("token") || localStorage.getItem("adminToken");
  const headers = {};
  if (!isFormData) headers["Content-Type"] = "application/json";
  if (token) {
    headers.Authorization =
      token.startsWith("Bearer ") || token.startsWith("Basic ")
        ? token
        : `Bearer ${token}`;
  }
  return headers;
};

/** Copy plain payload fields into FormData (objects are JSON-stringified) —
 * mirrors eventApi.js convention for multipart create/update. */
const appendPayloadToFormData = (formData, payload) => {
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === "object") {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });
};

const handleApiResponse = async (response) => {
  const contentType = response.headers.get("content-type");
  const isJson = contentType && contentType.includes("application/json");
  const result = isJson ? await response.json() : await response.text();

  const failed =
    !response.ok ||
    (typeof result === "object" &&
      result !== null &&
      (result.success === false || result.status === false));

  if (failed) {
    const errorMsg =
      (typeof result === "object" &&
        result !== null &&
        (result?.error?.message ||
          result?.message ||
          result?.error ||
          result?.details)) ||
      (typeof result === "string" && result.trim() ? result : null) ||
      `Web task request failed (${response.status})`;
    throw new Error(errorMsg);
  }

  return result;
};

const appendIf = (params, key, value) => {
  if (value !== undefined && value !== null && value !== "") {
    params.append(key, String(value));
  }
};

/**
 * §4.1 Get Web Tasks list
 * @param {Object} params
 * @param {number} [params.page=1]
 * @param {number} [params.limit=50] - `perPage` is accepted as an alias server-side
 * @param {boolean} [params.active] - true | false | undefined (all)
 * @param {string} [params.search] - matches title / taskId
 */
export const getWebTasks = async ({ page = 1, limit = 50, active, search } = {}) => {
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("limit", String(limit));
  if (active === true || active === false) params.append("active", String(active));
  if (search && String(search).trim()) params.append("search", String(search).trim());

  const response = await fetch(`${getEndpoint()}?${params.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleApiResponse(response);
};

/**
 * §4.2 Create Web Task
 * @param {Object} payload - { taskId, title, description, url, logoUrl, tag, tagColor,
 *   maxActiveSeconds, cooldownSeconds, dailyLimit, isActive, tiers[] }
 * @param {File} [logoFile] - optional logo image (multipart `image` field,
 *   mirrors the event/game module convention; backend stores it and exposes
 *   the public URL as logoUrl)
 */
export const createWebTask = async (payload, logoFile = null) => {
  if (logoFile) {
    const formData = new FormData();
    appendPayloadToFormData(formData, payload);
    formData.append("image", logoFile);
    const response = await fetch(`${getEndpoint()}/create`, {
      method: "POST",
      headers: getAuthHeaders(true),
      body: formData,
    });
    return handleApiResponse(response);
  }
  const response = await fetch(`${getEndpoint()}/create`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleApiResponse(response);
};

/**
 * §4.3 Update Web Task (partial). `id` may be web_tasks.id or taskId.
 * Tiers, when sent, replace the whole array.
 * @param {File} [logoFile] - optional logo image to replace (multipart `image`)
 */
export const updateWebTask = async (id, payload, logoFile = null) => {
  const path = `${getEndpoint(`${encodeURIComponent(String(id))}`)}`;
  if (logoFile) {
    const formData = new FormData();
    appendPayloadToFormData(formData, payload);
    formData.append("image", logoFile);
    const response = await fetch(path, {
      method: "PUT",
      headers: getAuthHeaders(true),
      body: formData,
    });
    return handleApiResponse(response);
  }
  const response = await fetch(path, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleApiResponse(response);
};

/**
 * Upload or replace a web task logo image.
 * POST /admin/web-task/{id}/image  (module-specific endpoint, mirrors
 * /admin/event/{id}/image and /admin/game/{id}/image)
 * @param {string|number} id
 * @param {File} file
 */
export const uploadWebTaskImage = async (id, file) => {
  const formData = new FormData();
  formData.append("image", file);
  const response = await fetch(
    `${getEndpoint(`${encodeURIComponent(String(id))}/image`)}`,
    {
      method: "POST",
      headers: getAuthHeaders(true),
      body: formData,
    }
  );
  return handleApiResponse(response);
};

/**
 * §4.4 Toggle task active / inactive.
 * Explicit `isActive` overrides; omit it (pass undefined) to invert the
 * current state server-side.
 */
export const toggleWebTaskStatus = async (id, isActive) => {
  const response = await fetch(
    `${getEndpoint(`${encodeURIComponent(String(id))}/status`)}`,
    {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(
        isActive === undefined || isActive === null ? {} : { isActive: !!isActive }
      ),
    }
  );
  return handleApiResponse(response);
};

/**
 * §4.5 Delete Web Task (soft delete — sets isActive = false, preserves history)
 */
export const deleteWebTask = async (id) => {
  const response = await fetch(
    `${getEndpoint(`${encodeURIComponent(String(id))}`)}`,
    {
      method: "DELETE",
      headers: getAuthHeaders(),
    }
  );
  return handleApiResponse(response);
};

/**
 * §4.6 Get single task details (same shape as one entry of §4.1 webTasks[]
 * with `stats`). `id` accepts web_tasks.id or taskId.
 */
export const getWebTaskDetail = async (id) => {
  const response = await fetch(
    `${getEndpoint(`detail/${encodeURIComponent(String(id))}`)}`,
    { method: "GET", headers: getAuthHeaders() }
  );
  return handleApiResponse(response);
};

/**
 * §4.7 Global Session Summary (dashboard). `byStatus` always lists all 7
 * status values in §6 order (zero-count included); `topUsers` ≤ 10 rows by
 * totalEp desc.
 * @param {Object} params
 * @param {string} [params.from] - UTC date (yyyy-mm-dd)
 * @param {string} [params.to] - UTC date (yyyy-mm-dd)
 */
export const getWebTaskSummary = async ({ from, to } = {}) => {
  const params = new URLSearchParams();
  appendIf(params, "from", from);
  appendIf(params, "to", to);
  const qs = params.toString();

  const response = await fetch(
    `${getEndpoint("summary")}${qs ? `?${qs}` : ""}`,
    { method: "GET", headers: getAuthHeaders() }
  );
  return handleApiResponse(response);
};

/**
 * §4.8 Session Audit List (alias: /history). By default lists finalized
 * sessions only; pass `includeOpen: true` to also return in-flight sessions
 * (completedAt null). `status` accepts any §6 value — `INSUFFICIENT` is a
 * filter alias for `INSUFFICIENT_PROGRESS`. Rows include denormalized
 * `taskTitle` / `taskLogo`.
 * @param {Object} params
 * @param {number} [params.page=1]
 * @param {number} [params.limit=50]
 * @param {string} [params.taskId]
 * @param {string} [params.status] - STARTED | COMPLETED | TIMEOUT | TIME_SPOOFED | CHEAT_SLEEP | BOT_CLICKS | INSUFFICIENT_PROGRESS (or alias INSUFFICIENT)
 * @param {string} [params.search] - username / email / userId / sessionId
 * @param {string} [params.from] - UTC date on completedAt
 * @param {string} [params.to] - UTC date on completedAt
 * @param {boolean} [params.includeOpen] - include in-flight sessions
 */
export const getWebTaskSessions = async ({
  page = 1,
  limit = 50,
  taskId,
  status,
  search,
  from,
  to,
  includeOpen,
} = {}) => {
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("limit", String(limit));
  appendIf(params, "taskId", taskId);
  appendIf(params, "status", status);
  appendIf(params, "search", search);
  appendIf(params, "from", from);
  appendIf(params, "to", to);
  if (includeOpen === true || includeOpen === "true" || includeOpen === 1) {
    params.append("includeOpen", "true");
  }

  const response = await fetch(`${getEndpoint("sessions")}?${params.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleApiResponse(response);
};

/**
 * §4.9 Per-User Web Task Data. `totalSessions` / `totalEp` count completed
 * sessions only; `currentEventPoints` / `currentEventRank` are 0 when no
 * event is running. `sessions[]` matches the §4.8 row shape minus user columns.
 * @param {string|number} userId
 * @param {Object} [params]
 * @param {string} [params.from] - UTC date on completedAt
 * @param {string} [params.to] - UTC date on completedAt
 */
export const getWebTaskUserDetail = async (userId, { from, to } = {}) => {
  const params = new URLSearchParams();
  appendIf(params, "from", from);
  appendIf(params, "to", to);
  const qs = params.toString();

  const response = await fetch(
    `${getEndpoint(`user/${encodeURIComponent(String(userId))}`)}${qs ? `?${qs}` : ""}`,
    { method: "GET", headers: getAuthHeaders() }
  );
  return handleApiResponse(response);
};
